import { useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { useMutation } from 'urql';
import { UPDATE_LOCATIONS_CUSTOMER_PICKUP } from 'src/graphql/requests/updateLocationsCustomerPickup';
import {
  GUpdateLocationsCustomerPickupMutation,
  GUpdateLocationsCustomerPickupMutationVariables,
} from 'src/graphql/types/generated';
import { useAppDispatch } from 'src/hooks/store';
import { logWarning } from 'src/services/datadog';
import {
  setCustomerPickupPending,
  updateRoutingLocations,
} from 'src/state/slices/locations';
import { hideModal, ModalType, showModal } from 'src/state/slices/modal';
import {
  NotificationType,
  showNotification,
} from 'src/state/slices/notification';
import { RootState } from 'src/state/store';
import { firstUserErrorMessage } from 'src/utils';

export type CustomerPickupTarget = {
  id: string;
  businessName?: string;
  // The value the table last showed for this location, where the caller knows
  // it. Every selected row is submitted, including ones already at the target,
  // and the server's reply says only where each row ended up, never where it
  // started. So a no-op re-send is told from a real change per row, here, or
  // not at all. Absent means "treat as a change", which is what the single-row
  // callers are.
  current?: boolean;
};

// Hands focus back to the control that opened the dialog. A disabled element
// refuses focus, and the bulk dialog's opener stays disabled until clearing the
// busy lock re-renders it, so a refused focus is retried after that paint.
// Without the retry the operator is dropped on the page body. The row switch is
// never disabled here, so it takes focus on the first try and skips the retry.
const returnFocusTo = (focusId: string): void => {
  const opener = document.getElementById(focusId);
  opener?.focus();
  if (opener && document.activeElement !== opener) {
    requestAnimationFrame(() => document.getElementById(focusId)?.focus());
  }
};

// Whether the operator's snapshot says this row moves to `customerPickup`.
export const isChange = (
  target: CustomerPickupTarget,
  customerPickup: boolean,
): boolean => target.current === undefined || target.current !== customerPickup;

export type SubmitCustomerPickupArgs = {
  // Every location the operator asked to write, not only the ones the table
  // predicts will change. The table's values are a snapshot that goes stale
  // when another operator or a backfill moves a row, while the server
  // re-derives the changing set itself and only guards that. Re-sending a row
  // that is already at the target costs nothing: it is a no-op write, and the
  // placement sweep fires only on a real change.
  locations: CustomerPickupTarget[];
  customerPickup: boolean;
  // The locations table's context id, captured when the switch opened the
  // dialog. A warehouse switch remounts the table and clears it, which is the
  // signal to abandon a write aimed at rows that are no longer on screen.
  contextId?: string;
  returnFocusId?: string;
  acknowledgeRecentDeliveries?: boolean;
  // Locations dropped by an earlier "skip these and flag the rest" round.
  // Carried so the final message can still name them: there is one toast slot,
  // so a later outcome would otherwise erase the record of what was left out.
  skipped?: CustomerPickupTarget[];
  // Locations an earlier round's reply called unknown. They are not re-sent,
  // since the answer would be the same, but they are still carried so the
  // final message names them. Dropping them would leave the operator thinking
  // their pickup setting was saved.
  unknown?: CustomerPickupTarget[];
};

// Ids are compared case-insensitively throughout, matching the backend, which
// echoes the caller's own spelling back in notFoundLocationIds while Postgres
// stores uuids lowercased.
export const sameId = (a: string, b: string): boolean =>
  a.toLowerCase() === b.toLowerCase();

const dedupeById = (
  targets: CustomerPickupTarget[],
): CustomerPickupTarget[] => {
  const seen = new Set<string>();
  return targets.filter((target) => {
    const key = target.id.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const nameOf = (target: CustomerPickupTarget): string =>
  target.businessName ?? 'an unnamed location';

const nameList = (targets: CustomerPickupTarget[]): string =>
  targets.map(nameOf).join(', ');

// One shared toast slot means each outcome has to arrive as a single message,
// so a skip that preceded it is appended rather than shown separately.
const skippedSentence = (skipped: CustomerPickupTarget[]): string =>
  skipped.length ? ` Skipped ${skipped.length}: ${nameList(skipped)}.` : '';

// The messages and the log identity live out here so `submit` stays inside the
// complexity budget: every singular/plural fork below would otherwise be a
// branch in a function that already carries the whole response protocol.
const nothingSavedMessage = (
  targets: CustomerPickupTarget[],
  notFound: CustomerPickupTarget[],
  skipped: CustomerPickupTarget[],
): string =>
  targets.length === 1
    ? `${
        targets[0].businessName ?? 'This location'
      } is not set up for delivery yet, so its pickup setting could not be saved.${skippedSentence(
        skipped,
      )}`
    : `No locations were saved. ${nameList(
        notFound,
      )} are not set up for delivery yet.${skippedSentence(skipped)}`;

const unconfirmedMessage = (targets: CustomerPickupTarget[]): string =>
  targets.length === 1
    ? 'The server did not confirm this location. Please retry; the displayed value is the last confirmed state.'
    : 'The server did not confirm every selected location. Please retry; the displayed values are the last confirmed state.';

const savedMessage = (
  targets: CustomerPickupTarget[],
  customerPickup: boolean,
  count: number,
): string => {
  if (targets.length === 1)
    return customerPickup
      ? 'Customer pickup turned on.'
      : 'Customer pickup turned off.';
  return `Customer pickup turned ${
    customerPickup ? 'on' : 'off'
  } for ${count} ${count === 1 ? 'location' : 'locations'}.`;
};

// The single-location payload is kept literally so the shipped alerting on it
// keeps matching.
const logIdentity = (
  targets: CustomerPickupTarget[],
): Record<string, unknown> =>
  targets.length === 1
    ? { locationId: targets[0].id }
    : {
        locationIds: targets.map((target) => target.id),
        count: targets.length,
      };

// The single-location shape the shipped review dialog and its tests already
// read, so they keep working untouched.
const singleLocationShape = (
  targets: CustomerPickupTarget[],
): Record<string, unknown> =>
  targets.length === 1
    ? { locationId: targets[0].id, businessName: targets[0].businessName }
    : {};

// A rejection re-renders the review dialog through the same modal slice and
// modal type. Without a changing key React keeps the mounted instance, and its
// mount-time snapshot of the offender list with it, so a second rejection would
// show the first one's offenders and skipping would loop forever. A module
// counter rather than crypto.randomUUID, which this jsdom does not provide, or
// Date.now(), which repeats when a mocked mutation resolves synchronously.
let nonce = 0;
const nextNonce = (): number => {
  nonce += 1;
  return nonce;
};

// Shared by the confirmation dialog, the bulk tab and the recent-deliveries
// dialog, so an acknowledged or partial re-submit takes exactly the path the
// first attempt took.
//
// Branch order is load-bearing. RecentDeliveriesPresent has to be inspected
// before the generic userErrors handling, or the rejection the acknowledge flow
// exists to surface collapses into an error toast and the offender list is
// never shown.
export const useUpdateLocationCustomerPickup = (): {
  submit: (args: SubmitCustomerPickupArgs) => Promise<void>;
  isSubmitting: boolean;
} => {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitting = useRef(false);
  const [, update] = useMutation<
    GUpdateLocationsCustomerPickupMutation,
    GUpdateLocationsCustomerPickupMutationVariables
  >(UPDATE_LOCATIONS_CUSTOMER_PICKUP);

  const submit = async ({
    locations,
    customerPickup,
    contextId,
    returnFocusId,
    acknowledgeRecentDeliveries,
    skipped = [],
    unknown = [],
  }: SubmitCustomerPickupArgs): Promise<void> => {
    if (submitting.current) return;
    let returnFocusOnClose = false;
    const targets = dedupeById(locations);
    if (!targets.length) return;
    submitting.current = true;
    setIsSubmitting(true);
    // Holds the edit-locations dialog and the table's Edit button shut for the
    // duration. Context-scoped, so a write abandoned by a warehouse switch
    // cannot release the lock a write in the new context is holding.
    if (contextId)
      dispatch(setCustomerPickupPending({ contextId, pending: true }));

    // A GraphQL 200 carrying userErrors is not a failed request, so
    // forwardErrorsToLogs never sees it. Flagging drives where every future
    // order for these locations is placed, so a systematic failure has to be
    // visible in prod, not only in the operator's toast.
    const warn = (
      userError: string | undefined,
      transportError: boolean,
    ): void => {
      logWarning('customer_pickup.location_flag_failed', {
        ...logIdentity(targets),
        customerPickup,
        acknowledged: Boolean(acknowledgeRecentDeliveries),
        userError,
        transportError,
      });
    };

    const fail = (message: string | undefined): void => {
      dispatch(showNotification({ type: NotificationType.Error, message }));
    };

    try {
      const { data, error } = await update({
        input: {
          locationIds: targets.map((target) => target.id),
          customerPickup,
          ...(acknowledgeRecentDeliveries
            ? { acknowledgeRecentDeliveries: true }
            : {}),
        },
      });

      // The table remounted (warehouse switch) while this was in flight. Its
      // rows are gone, so there is nothing to write back to and no one to
      // message.
      if (
        contextId &&
        store.getState().locations.metrobi?.contextId !== contextId
      ) {
        dispatch(hideModal());
        return;
      }

      const payload = data?.updateLocationsCustomerPickup;
      const userErrors = payload?.userErrors ?? [];

      if (error || !payload) {
        warn(undefined, Boolean(error));
        fail(error?.message);
        return;
      }

      const notFound = targets.filter((target) =>
        payload.notFoundLocationIds.some((id) => sameId(id, target.id)),
      );

      const recentDeliveries = userErrors.find(
        (userError) => userError.__typename === 'RecentDeliveriesPresent',
      );
      if (recentDeliveries?.__typename === 'RecentDeliveriesPresent') {
        // Nothing was written: the guard raises before the transaction opens.
        // Hand the list to the review dialog so ops can decide per location.
        dispatch(
          showModal({
            type: ModalType.CustomerPickupRecentDeliveries,
            // Without this the slice falls back to "Okay", which reads as
            // agreement on a dialog whose whole point is that declining is a
            // real choice.
            dismissButtonLabel: 'Cancel',
            variables: {
              locations: targets,
              // The rejection reports unknown ids too. Those can never change,
              // so the dialog needs them to leave them out of what it counts
              // and of what a skip would re-send.
              notFoundIds: notFound.map((target) => target.id),
              skipped,
              unknown: dedupeById([...unknown, ...notFound]),
              contextId,
              returnFocusId,
              offenders: recentDeliveries.offenders,
              requestNonce: nextNonce(),
              ...singleLocationShape(targets),
            },
          }),
        );
        return;
      }

      if (userErrors.length > 0) {
        warn(userErrors[0]?.__typename, false);
        fail(firstUserErrorMessage(userErrors));
        return;
      }

      // A location exists in the delivery service only after the location sync,
      // so an id can be valid upstream and still be unknown here. Nothing was
      // written for those, but in a bulk write the rest were, so the unknown
      // ones are reported without discarding the successes.
      const requested = targets.filter(
        (target) => !notFound.some((missing) => sameId(missing.id, target.id)),
      );

      // Anything this reply calls unknown, plus anything an earlier round did.
      // Carried ones are not re-sent, so this is the only place they surface.
      const unsaved = dedupeById([...unknown, ...notFound]);

      if (!requested.length) {
        warn('NotFound', false);
        fail(nothingSavedMessage(targets, unsaved, skipped));
        return;
      }

      const confirmed = requested.filter((target) =>
        payload.locations.some(
          (location) =>
            sameId(location.id, target.id) &&
            location.customerPickup === customerPickup,
        ),
      );
      if (confirmed.length !== requested.length) {
        warn('Unconfirmed', false);
        fail(unconfirmedMessage(targets));
        return;
      }

      dispatch(
        updateRoutingLocations(
          confirmed.map((target) => ({
            locationId: target.id,
            customerPickup,
          })),
        ),
      );

      // Only the rows that moved. Counting every confirmed row would report a
      // selection of ten already-flagged locations plus one new one as eleven
      // changes.
      const saved = savedMessage(
        targets,
        customerPickup,
        confirmed.filter((target) => isChange(target, customerPickup)).length,
      );

      if (unsaved.length) {
        if (notFound.length) warn('NotFound', false);
        fail(
          `${saved} Not saved: ${nameList(
            unsaved,
          )} (not set up for delivery yet).${skippedSentence(skipped)}`,
        );
      } else {
        dispatch(
          showNotification({
            type: NotificationType.Success,
            message: `${saved}${skippedSentence(skipped)}`,
          }),
        );
      }

      dispatch(hideModal());
      // Confirming closes the dialog the same way cancelling does, so it owes
      // the same focus return. The rejection path deliberately skips this: it
      // opens the recent-deliveries dialog instead of closing, and focus
      // belongs there. The focus itself waits for the finally below.
      returnFocusOnClose = true;
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
      if (contextId)
        dispatch(setCustomerPickupPending({ contextId, pending: false }));
      if (returnFocusOnClose && returnFocusId) returnFocusTo(returnFocusId);
    }
  };

  return { submit, isSubmitting };
};
